import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Clock, Wrench, Eye } from "lucide-react";

const BADGE = "/brand/badge_trim.png";

function pad(n) { return String(n).padStart(2, "0"); }

function Countdown({ target }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, new Date(target).getTime() - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const Box = ({ v, l }) => (
    <div className="flex flex-col items-center">
      <span className="font-serif font-extrabold text-3xl md:text-4xl text-[#2B170C] tabular-nums w-16 md:w-20 text-center bg-white rounded-2xl py-2 border border-[#EBD9C4] shadow-sm">{pad(v)}</span>
      <span className="text-[11px] uppercase tracking-wider text-[#7A6455] mt-1.5">{l}</span>
    </div>
  );
  return (
    <div data-testid="maintenance-countdown" className="flex items-center gap-3 md:gap-4 mt-8">
      {d > 0 && <Box v={d} l="jours" />}
      <Box v={h} l="heures" />
      <Box v={m} l="min" />
      <Box v={s} l="sec" />
    </div>
  );
}

function MaintenancePage({ m }) {
  const reopen = m?.reopen_at;
  const future = reopen && new Date(reopen).getTime() > Date.now();
  return (
    <div data-testid="maintenance-page" className="min-h-screen bg-[#FAF6F0] grid place-items-center px-5 py-16 text-center">
      <div className="max-w-xl flex flex-col items-center">
        <img src={BADGE} width="743" height="383" alt="Crunchy Sweet" className="w-24 md:w-28 mb-6 opacity-95" />
        <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold text-[#C6531A]">
          <Wrench size={14} /> Maintenance
        </span>
        <h1 className="font-serif font-extrabold text-4xl md:text-5xl text-[#2B170C] mt-4 leading-[1.05]">
          {m?.title || "Nous revenons très vite \u2764"}
        </h1>
        <p className="mt-5 text-base md:text-lg text-[#6E5544] leading-relaxed">
          {m?.message || "Le site est en cours de mise à jour. Rendez-vous très bientôt pour de nouvelles gourmandises !"}
        </p>
        {future && (
          <>
            <p className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#5A3620]">
              <Clock size={16} className="text-[#C6531A]" /> Réouverture dans
            </p>
            <Countdown target={reopen} />
          </>
        )}
        <p className="mt-10 text-sm text-[#7A6455]">Crunchy Sweet · Palaiseau (91)</p>
      </div>
    </div>
  );
}

export function MaintenancePreview() {
  const { settings } = useApp();
  return <MaintenancePage m={settings?.maintenance || {}} />;
}

// Les pages légales restent toujours accessibles, même pendant la maintenance.
const LEGAL_PATHS = ["/mentions-legales", "/cgv", "/confidentialite", "/politique-cookies", "/livraison-retrait", "/allergenes", "/mediation"];

export default function MaintenanceGate({ children }) {
  const { settings, user } = useApp();
  const loc = useLocation();
  const m = settings?.maintenance;
  const active = !!(m?.enabled && (!m.reopen_at || new Date() < new Date(m.reopen_at)));
  const isAdmin = user?.role === "admin";
  const allowPath = loc.pathname.startsWith("/admin") || loc.pathname === "/connexion" || LEGAL_PATHS.includes(loc.pathname);
  if (active && !isAdmin && !allowPath) return <MaintenancePage m={m} />;
  if (active && isAdmin && !loc.pathname.startsWith("/admin") && loc.pathname !== "/apercu-maintenance") {
    return (
      <>
        <div data-testid="maintenance-admin-banner" role="status" className="relative z-[60] bg-[#2B170C] text-[#FFF8F1] text-xs sm:text-sm px-4 py-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center">
          <span className="inline-flex items-center gap-1.5 font-semibold"><Eye size={15} className="text-[#F4A06C]" /> Mode aperçu</span>
          <span className="opacity-85">Le site est en maintenance : tes visiteurs voient la page d'attente, toi tu vois le vrai site.</span>
          <a href="/apercu-maintenance" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 text-[#F4A06C] font-semibold" data-testid="maintenance-banner-preview">Voir la page d'attente</a>
        </div>
        {children}
      </>
    );
  }
  return children;
}
